import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { isPamyatniki } from '../lib/variant';

const useStore = create(
  persist(
    (set, get) => ({
      // Shared project view mode (menus collapsed)
      isSharedView: false,
      setSharedView: (value) => set({ isSharedView: value }),

      // Embed mode (?embed=1): hide all UI, show only 3D scene. Driven by quiz iframe.
      embedMode: false,
      setEmbedMode: (value) => set({ embedMode: value }),

      // Quiz view mode: signal from the iframe quiz so Scene3D can adjust camera
      // and overlays per step. 'size' = side 3/4 ракурс + силуэт человека 170см.
      quizViewMode: null,
      setQuizViewMode: (mode) => set({ quizViewMode: mode || null }),

      // Mobile sidebar open state (used to shrink 3D scene)
      mobileSidebarOpen: false,
      setMobileSidebarOpen: (value) => set({ mobileSidebarOpen: value }),
      
      // Perimeter settings
      perimeterWidth: 2,
      perimeterLength: 3,
      setPerimeterSize: (width, length) => set({ perimeterWidth: width, perimeterLength: length }),
      
      // Categories and elements from API (not persisted - loaded from API)
      categories: [],
      elements: [],
      setCategories: (categories) => set({ categories }),
      setElements: (elements) => set({ elements }),
      
      // Placed elements in scene
      placedElements: [],
      addPlacedElement: (element) => set((state) => ({
        placedElements: [...state.placedElements, {
          ...element,
          id: `placed-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          position: { x: 0, y: 0, z: 0 },
          rotation: 0
        }]
      })),
      removePlacedElement: (id) => set((state) => ({
        placedElements: state.placedElements.filter(el => el.id !== id)
      })),
      updatePlacedElement: (id, updates) => set((state) => ({
        placedElements: state.placedElements.map(el =>
          el.id === id ? { ...el, ...updates } : el
        )
      })),
      clearPlacedElements: () => set({
        placedElements: [],
        selectedFloor: null,
        selectedFoundation: null,
        selectedFence: null,
        selectedBorder: null,
        customItems: [],
        flowerbedFilling: null
      }),
      
      // Custom items (free-form entries like delivery, installation)
      customItems: [],
      addCustomItem: (item) => set((state) => ({
        customItems: [...state.customItems, {
          ...item,
          id: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        }]
      })),
      removeCustomItem: (id) => set((state) => ({
        customItems: state.customItems.filter(item => item.id !== id)
      })),
      
      // Pending monument: выбран цвет в сайдбаре, но ещё не добавлен на сцену (пока не нажали «Выбрать»)
      pendingMonument: null,
      setPendingMonument: (payload) => set({ pendingMonument: payload }),
      clearPendingMonument: () => set({ pendingMonument: null }),
      
      // Pending обычный объект: пользователь "примеряет" любой элемент каталога перед добавлением на сцену
      pendingElement: null,
      setPendingElement: (payload) => set({ pendingElement: payload }),
      clearPendingElement: () => set({ pendingElement: null }),
      
      // Selected placed element (for transform controls)
      selectedPlacedId: null,
      setSelectedPlacedId: (id) => set({ selectedPlacedId: id }),
      
      // Transform mode
      transformMode: 'translate',
      setTransformMode: (mode) => set({ transformMode: mode }),
      
      // Floor and foundation selection
      selectedFloor: null,
      setSelectedFloor: (floor) => set({ selectedFloor: floor }),
      flowerbedFilling: null, // null = Земля (бесплатно), или объект напольного покрытия
      setFlowerbedFilling: (filling) => set({ flowerbedFilling: filling }),
      selectedFoundation: null,
      setSelectedFoundation: (foundation) => set({ selectedFoundation: foundation }),
      
      // Pedestal category elements are now regular placed elements (draggable)
      // No global selectedPedestal layer anymore
      
      // Fence (perimeter-based)
      selectedFence: null,
      setSelectedFence: (fence) => set({ selectedFence: fence }),
      
      // Border (perimeter-based)
      selectedBorder: null,
      setSelectedBorder: (border) => set({ selectedBorder: border }),
      
      // Camera reset callback (set by Scene3D)
      resetCameraCallback: null,
      setResetCameraCallback: (callback) => set({ resetCameraCallback: callback }),
      
      // Loading state for GLB models
      loadingModels: {}, // { elementId: true/false }
      setModelLoading: (elementId, isLoading) => set((state) => ({
        loadingModels: { ...state.loadingModels, [elementId]: isLoading }
      })),
      clearModelLoading: (elementId) => set((state) => {
        const { [elementId]: _, ...rest } = state.loadingModels;
        return { loadingModels: rest };
      }),
      isAnyModelLoading: () => Object.values(get().loadingModels).some(Boolean),
      
      // Get perimeter length in meters
      getPerimeterLength: () => {
        const state = get();
        return 2 * (state.perimeterWidth + state.perimeterLength);
      },
      
      // Calculate total price
      getTotalPrice: () => {
        const state = get();
        let total = 0;
        
        state.placedElements.forEach(el => {
          const priceUnit = el.price_unit || 'piece';
          const basePrice = el.price || 0;
          
          if (priceUnit === 'cubic_meter') {
            // Полый цветник: объём только камня (3 балки). Только если явно цветник и не rectangle
            const w = el.width || 1, h = el.height || 1, d = el.depth || 1;
            const isPShape = el.model_type === 'flowerbed' && el.flowerbed_shape != null && el.flowerbed_shape !== 'rectangle';
            const volume = isPShape ? 0.08 * h * (2 * d + w) : w * h * d;
            total += basePrice * volume;
          } else if (priceUnit === 'linear_meter') {
            // Price per linear meter (using width as length)
            total += basePrice * (el.width || 1);
          } else if (priceUnit === 'sqmeter') {
            // Price per square meter (width × depth)
            const area = (el.width || 1) * (el.depth || 1);
            total += basePrice * area;
          } else if (priceUnit === 'sqmeter_wh') {
            // Price scales proportionally from default size.
            // basePrice = price at default dimensions stored in catalog.
            const origEl = state.elements.find(e => e.id === (el.element_id || el.id));
            const defaultArea = origEl ? (origEl.width || 1) * (origEl.height || 1) : 1;
            const currentArea = (el.width || 1) * (el.height || 1);
            total += basePrice * (currentArea / defaultArea);
          } else {
            // Price per piece (fixed price)
            total += basePrice;
          }

          // Pamyatniki: плоская доплата за форму (например, резка арки у «Мусульманской»).
          // Задаётся в админке в поле «Цена установки» элемента. Объёмная цена у фигурных
          // форм меньше, а работа дороже — эта надбавка это компенсирует. У обычных стел
          // поле = 0, так что на них не влияет. Для blago ветка сворачивается (байт-идентично).
          if (isPamyatniki) total += el.installation_price || 0;
        });

        // Flowerbed filling (using selected filling price for internal area of each flowerbed)
        if (state.flowerbedFilling) {
          const fillPrice = Number(state.flowerbedFilling.price) || 0;
          state.placedElements.forEach(el => {
            // Наполнение считается только для П-образного (полого) цветника.
            // Для "Цельный" (rectangle) наполнения нет.
            if (el.model_type === 'flowerbed' && el.flowerbed_shape != null && el.flowerbed_shape !== 'rectangle') {
              const borderThick = 0.08;
              const innerW = Math.max(0, (el.width || 1) - 2 * borderThick);
              const innerD = Math.max(0, (el.depth || 1) - borderThick);
              total += fillPrice * innerW * innerD;
            }
          });
        }

        if (state.selectedFloor) {
          const area = state.perimeterWidth * state.perimeterLength;
          total += (state.selectedFloor.price || 0) * area;
        }
        
        if (state.selectedFoundation) {
          const area = state.perimeterWidth * state.perimeterLength;
          total += (state.selectedFoundation.price || 0) * area;
        }
        
        if (state.selectedBorder) {
          const perimeterLength = 2 * (state.perimeterWidth + state.perimeterLength);
          const unit = state.selectedBorder.price_unit || 'linear_meter';
          if (unit === 'cubic_meter') {
            // Бордюр из камня: считаем объём облицовки (толщина 2см по умолчанию)
            const h = Number(state.selectedBorder.height) || 0.1;
            const t = Number(state.selectedBorder.pricing_thickness_m) || 0.02;
            const volume = perimeterLength * h * t;
            total += (state.selectedBorder.price || 0) * volume;
          } else {
            // Бордюр "за метр" (как бетонный) — оставляем как было
            total += (state.selectedBorder.price || 0) * perimeterLength;
          }
        }
        
        if (state.selectedFence) {
          const perimeterLength = 2 * (state.perimeterWidth + state.perimeterLength);
          total += (state.selectedFence.price || 0) * perimeterLength;
        }
        
        // Add custom items
        state.customItems.forEach(item => {
          total += item.price || 0;
        });

        // Installation costs
        total += state.getTotalInstallationPrice();

        return total;
      },

      getTotalInstallationPrice: () => {
        const state = get();
        const perimeterMeters = 2 * (state.perimeterWidth + state.perimeterLength);
        const plotArea = state.perimeterWidth * state.perimeterLength;
        let total = 0;

        const getCatInstall = (categoryId) => {
          const cat = state.categories.find(c => c.id === categoryId);
          return (cat?.installation_price) || 0;
        };
        const getCatByName = (name) => state.categories.find(c => c.name === name);

        // Placed elements — per piece per category
        state.placedElements.forEach(el => {
          total += getCatInstall(el.category_id);
        });

        // Floor (покрытие) — per m², берём из элемента (у каждого своя цена укладки)
        if (state.selectedFloor) {
          total += (state.selectedFloor.installation_price || 0) * plotArea;
        }

        // Foundation (фундамент) — per m²
        if (state.selectedFoundation) {
          const cat = getCatByName('foundation');
          total += (cat?.installation_price || 0) * plotArea;
        }

        // Border (бордюр) — per linear meter
        if (state.selectedBorder) {
          const cat = getCatByName('border');
          total += (cat?.installation_price || 0) * perimeterMeters;
        }

        // Fence (оградка) — per linear meter
        if (state.selectedFence) {
          const cat = getCatByName('fence');
          total += (cat?.installation_price || 0) * perimeterMeters;
        }

        return total;
      },
      
      // Get project data for export
      getProjectData: () => {
        const state = get();
        const area = state.perimeterWidth * state.perimeterLength;
        const perimeterLength = 2 * (state.perimeterWidth + state.perimeterLength);
        
        return {
          perimeter_width: state.perimeterWidth,
          perimeter_length: state.perimeterLength,
          elements: state.placedElements.map(el => ({
            id: el.id,
            element_id: el.element_id || el.id,
            name_ru: el.name_ru,
            // Важно: для PDF нужно то же, что для расчёта на экране (unit + размеры + категория)
            category_id: el.category_id,
            price: el.price,
            price_unit: el.price_unit || 'piece',
            width: el.width,
            height: el.height,
            depth: el.depth,
            flowerbed_shape: el.flowerbed_shape,
            colorName: el.colorName,
            model_type: el.model_type,
            lastName: el.lastName,
            firstName: el.firstName,
            patronymic: el.patronymic,
            position_x: el.position.x,
            position_y: el.position.y,
            position_z: el.position.z,
            rotation_y: el.rotation
          })),
          floor: state.selectedFloor ? {
            id: state.selectedFloor.id,
            name_ru: state.selectedFloor.name_ru,
            price: state.selectedFloor.price * area
          } : null,
          foundation: state.selectedFoundation ? {
            id: state.selectedFoundation.id,
            name_ru: state.selectedFoundation.name_ru,
            price: state.selectedFoundation.price * area
          } : null,
          border: state.selectedBorder ? {
            id: state.selectedBorder.id,
            name_ru: state.selectedBorder.name_ru,
            // Важно: цена в экспорт/ПДФ должна совпадать с экраном
            price: (state.selectedBorder.price_unit === 'cubic_meter')
              ? Math.round((state.selectedBorder.price || 0) * perimeterLength * (Number(state.selectedBorder.height) || 0.1) * (Number(state.selectedBorder.pricing_thickness_m) || 0.02))
              : state.selectedBorder.price * perimeterLength,
            perimeter_length: perimeterLength
          } : null,
          fence: state.selectedFence ? {
            id: state.selectedFence.id,
            name_ru: state.selectedFence.name_ru,
            price: state.selectedFence.price * perimeterLength,
            perimeter_length: perimeterLength
          } : null,
          custom_items: state.customItems,
          total_price: state.getTotalPrice(),
          created_at: new Date().toISOString()
        };
      },
      
      // Auth state
      token: localStorage.getItem('admin_token'),
      isAuthenticated: !!localStorage.getItem('admin_token'),
      setToken: (token) => {
        if (token) {
          localStorage.setItem('admin_token', token);
        } else {
          localStorage.removeItem('admin_token');
        }
        set({ token, isAuthenticated: !!token });
      },
      logout: () => {
        localStorage.removeItem('admin_token');
        set({ token: null, isAuthenticated: false });
      }
    }),
    {
      name: 'iritual-designer-storage',
      partialize: (state) => ({
        // Only persist these fields
        perimeterWidth: state.perimeterWidth,
        perimeterLength: state.perimeterLength,
        placedElements: state.placedElements,
        selectedFloor: state.selectedFloor,
        selectedFoundation: state.selectedFoundation,
        selectedFence: state.selectedFence,
        selectedBorder: state.selectedBorder,
        customItems: state.customItems,
        // Наполнение цветника (Земля/покрытие) — чтобы выбор не сбрасывался при перезагрузке страницы
        flowerbedFilling: state.flowerbedFilling,
      }),
      merge: (persistedState, currentState) => {
        // Если в URL есть ?project= — не восстанавливаем сцену из localStorage,
        // иначе оградки/элементы прошлого проекта мелькают до загрузки нового.
        // То же в embed-режиме: iframe-предпросмотр квиза должен стартовать
        // с чистой сцены, а не с того, что юзер двигал в прошлой сессии.
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('project') || urlParams.get('embed') === '1') {
          const { selectedFence, selectedFoundation, selectedFloor, selectedBorder, placedElements, customItems, ...rest } = persistedState;
          return { ...currentState, ...rest };
        }
        return { ...currentState, ...persistedState };
      },
    }
  )
);

export default useStore;
